import React from 'react';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900">
      {/* Custom Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        
        .font-display { 
          font-family: 'Space Grotesk', system-ui, sans-serif; 
          letter-spacing: -0.04em;
          font-feature-settings: "liga", "kern";
        }
        .font-body { 
          font-family: 'Inter', system-ui, sans-serif; 
          font-feature-settings: "liga", "kern";
          line-height: 1.65;
        }
        
        .text-gradient-purple {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .glass-premium {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08), 0 1px 0 rgba(255, 255, 255, 0.4) inset;
        }
        
        .google-limited-use {
          background: #f0f9ff;
          border: 2px solid #0ea5e9;
          border-radius: 12px;
          padding: 24px;
          margin: 24px 0;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-4 left-4 right-4 md:top-8 md:left-1/2 md:transform md:-translate-x-1/2 md:right-auto z-50 glass-premium backdrop-blur-xl rounded-2xl md:rounded-full px-4 md:px-8 py-3 md:py-4 shadow-2xl max-w-none md:max-w-fit">
        <div className="flex items-center justify-between md:gap-10">
          <div className="font-display text-lg md:text-xl text-gradient-purple">
            receptioneer.ai
          </div>
          <div className="hidden lg:flex items-center gap-8">
            <a href="/" className="font-body text-slate-600 hover:text-indigo-600 transition-colors font-medium">
              Home
            </a>
            <a href="/terms" className="font-body text-slate-600 hover:text-indigo-600 transition-colors font-medium">
              Terms
            </a>
          </div>
          <a 
            href="/"
            className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold transition-all duration-300 text-sm md:text-base hover:shadow-lg"
          >
            Back to Home
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-normal mb-6 text-slate-800 leading-tight">
              Privacy Policy
            </h1>
            <div className="inline-flex items-center gap-2 glass-premium rounded-full px-6 py-3 text-slate-600 font-medium text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              Effective January 1, 2025
            </div>
          </div>

          <div className="glass-premium rounded-2xl p-8 md:p-12 shadow-xl">
            <div className="prose prose-slate max-w-none font-body">
              
            <div className="google-limited-use">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Google API Services - Limited Use Disclosure
                </h2>
                
                <div className="bg-blue-100 border-l-4 border-blue-500 p-4 mb-4">
                  <p className="text-slate-800 font-bold text-lg mb-2">
                    ✓ AFFIRMATIVE STATEMENT OF COMPLIANCE
                  </p>
                  <p className="text-slate-700 font-semibold mb-3">
                    receptioneer.ai's use and transfer to any other app of information received from Google APIs adheres to the{' '}
                    <a 
                      href="https://developers.google.com/workspace/workspace-api-user-data-developer-policy#limited-use" 
                      className="text-blue-600 hover:text-blue-800 underline font-bold"
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Limited Use requirements
                    </a>
                    {' '}of the{' '}
                    <a 
                      href="https://developers.google.com/workspace/workspace-api-user-data-developer-policy" 
                      className="text-blue-600 hover:text-blue-800 underline font-bold"
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Workspace API User Data and Developer Policy
                    </a>
                  </p>
                </div>

                <div className="bg-white/60 rounded-lg p-4 mt-4">
                  <h3 className="font-semibold text-slate-800 mb-2">Our Commitment:</h3>
                  <ul className="space-y-2 text-slate-700">
                    <li>✓ We use Google Calendar data ONLY to schedule appointments for your business</li>
                    <li>✓ We do NOT use your Google data for advertising, marketing, or any other purposes</li>
                    <li>✓ We do NOT sell, share, or transfer your Google data to third parties</li>
                    <li>✓ We do NOT use Google data to train AI models beyond your specific business needs</li>
                  </ul>
                </div>
              </div>
              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Introduction</h2>
                <p className="text-slate-600 leading-relaxed">
                  receptioneer.ai powered by nodayoff ("we," "our," or "us") operates an AI-powered receptionist service for service businesses. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Information We Collect</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Information You Provide</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li><strong>Account Information:</strong> Name, email address, phone number, business name, and billing information</li>
                    <li><strong>Business Configuration:</strong> Service offerings, pricing, hours of operation, service areas, and appointment preferences</li>
                    <li><strong>Google Calendar Access:</strong> With your explicit permission, we access your Google Calendar SOLELY to:
                      <ul className="ml-6 mt-2 space-y-1">
                        <li>• Read your availability to prevent double-booking</li>
                        <li>• Create appointment events when customers book through our AI receptionist</li>
                        <li>• Update or cancel appointments as requested by customers</li>
                      </ul>
                    </li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Google API Specific Data Collection</h3>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-slate-700 mb-3"><strong>When you connect your Google Calendar, we access:</strong></p>
                    <ul className="space-y-2 text-slate-600">
                      <li>• Calendar events (title, time, duration, location) - ONLY for availability checking</li>
                      <li>• Your calendar's free/busy status</li>
                      <li>• Basic calendar metadata (timezone, calendar name)</li>
                    </ul>
                    <p className="text-slate-700 mt-3"><strong>We do NOT access:</strong></p>
                    <ul className="space-y-2 text-slate-600">
                      <li>• Event descriptions or private notes</li>
                      <li>• Attendee lists or contact information</li>
                      <li>• Any other Google services (Gmail, Drive, Photos, etc.)</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Information Collected Through Service Use</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li><strong>Call Recordings:</strong> Phone conversations handled by our AI receptionist for quality assurance and service improvement</li>
                    <li><strong>Call Metadata:</strong> Phone numbers, call duration, timestamps, and call outcomes</li>
                    <li><strong>Customer Information:</strong> Names, phone numbers, and service requests from callers</li>
                    <li><strong>Usage Analytics:</strong> Dashboard interactions and feature usage (NOT Google data analytics)</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">How We Use Your Information</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Google Calendar Data Usage - STRICTLY LIMITED</h3>
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-slate-700 mb-3"><strong>We use Google Calendar data EXCLUSIVELY to:</strong></p>
                    <ul className="space-y-2 text-slate-600">
                      <li>✓ Check your availability when customers request appointments</li>
                      <li>✓ Create calendar events for confirmed appointments</li>
                      <li>✓ Update or cancel appointments as requested</li>
                      <li>✓ Send you notifications about appointment changes</li>
                    </ul>
                    <p className="text-red-700 mt-3"><strong>We NEVER use Google Calendar data to:</strong></p>
                    <ul className="space-y-2 text-slate-600">
                      <li>✗ Train general AI models or improve AI for other customers</li>
                      <li>✗ Create marketing profiles or audience segments</li>
                      <li>✗ Share with third parties for any purpose</li>
                      <li>✗ Analyze your business patterns beyond appointment scheduling</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Primary Service Delivery</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>Operate the AI receptionist service for your specific business</li>
                    <li>Answer calls and handle customer inquiries</li>
                    <li>Book appointments using your connected calendar</li>
                    <li>Send SMS notifications about calls and appointments</li>
                    <li>Provide analytics about YOUR business calls only</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">AI Model Training - Important Limitations</h3>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-slate-700 mb-2"><strong>How we train our AI:</strong></p>
                    <ul className="space-y-2 text-slate-600">
                      <li>• We train AI models using call recordings to improve understanding of YOUR specific business</li>
                      <li>• Training is isolated to YOUR instance only - not shared across customers</li>
                      <li>• Google Calendar data is NEVER used for AI training</li>
                      <li>• Customer personal information is anonymized before any training</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Information Sharing and Disclosure</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Google Data Sharing - STRICTLY PROHIBITED</h3>
                  <div className="bg-red-50 rounded-lg p-4">
                    <p className="text-slate-700 font-semibold">We NEVER share your Google Calendar data with:</p>
                    <ul className="space-y-2 text-slate-600 mt-2">
                      <li>✗ Other customers or businesses</li>
                      <li>✗ Third-party services (except Google's own APIs)</li>
                      <li>✗ Marketing or advertising platforms</li>
                      <li>✗ Data brokers or analytics companies</li>
                      <li>✗ AI training datasets outside your specific instance</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Limited Sharing Scenarios:</h3>
                  <div className="space-y-4 text-slate-600">
                    <p><strong>With Your Customers:</strong> Basic business info (hours, services) as part of receptionist duties - NO Google data</p>
                    <p><strong>Service Providers:</strong> Infrastructure providers (AWS, etc.) with strict data processing agreements</p>
                    <p><strong>Legal Requirements:</strong> Only when legally required, with notice to you when permitted</p>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Data Security</h2>
                <div className="mb-4">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Google Data Security</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li><strong>OAuth 2.0:</strong> We never store your Google password</li>
                    <li><strong>Encrypted Tokens:</strong> Google access tokens are encrypted at rest</li>
                    <li><strong>Minimal Access:</strong> We request only the minimum necessary Google Calendar scopes</li>
                    <li><strong>Regular Audits:</strong> Security reviews of our Google API integration</li>
                  </ul>
                </div>
                <div className="mt-4">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">General Security Measures</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>End-to-end encryption for all data</li>
                    <li>SOC 2 Type II compliant infrastructure</li>
                    <li>Regular security assessments</li>
                    <li>Employee access controls and training</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Data Retention</h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-800 mb-3">Google Calendar Data:</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>• We do NOT store your calendar events - we read them in real-time</li>
                    <li>• Appointment data we create is stored only as business records</li>
                    <li>• Access tokens are deleted immediately upon disconnection</li>
                  </ul>
                </div>
                <div className="mt-4">
                  <h3 className="font-semibold text-slate-800 mb-3">Other Data:</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>Call recordings: 2 years</li>
                    <li>Account information: Active period + 3 years</li>
                    <li>Customer data: 1 year after account termination</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Your Rights and Choices</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Google Calendar Control</h3>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <ul className="space-y-2 text-slate-600">
                      <li><strong>Revoke Access:</strong> Disconnect Google Calendar anytime from your dashboard</li>
                      <li><strong>Manage Permissions:</strong> Review our access at <a href="https://myaccount.google.com/permissions" className="text-blue-600 hover:text-blue-800 underline">Google Account Permissions</a></li>
                      <li><strong>Scope Review:</strong> We only request: calendar.events and calendar.readonly</li>
                      <li><strong>Immediate Effect:</strong> Revocation stops all calendar access instantly</li>
                    </ul>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">General Privacy Rights</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>Access and download your data</li>
                    <li>Correct inaccurate information</li>
                    <li>Request deletion of your data</li>
                    <li>Opt-out of marketing communications</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">California Privacy Rights (CCPA)</h3>
                  <p className="text-slate-600">California residents have additional rights including the right to know, delete, and opt-out of data sales (though we don't sell data).</p>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">European Privacy Rights (GDPR)</h3>
                  <p className="text-slate-600">EU/UK residents have rights including access, rectification, erasure, portability, and objection to processing.</p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Third-Party Services</h2>
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-semibold text-slate-800 mb-3">Google API Services</h3>
                  <p className="text-slate-600 mb-2">Our use of Google Calendar is governed by:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li>• <a href="https://policies.google.com/privacy" className="text-blue-600 hover:text-blue-800 underline">Google Privacy Policy</a></li>
                    <li>• <a href="https://developers.google.com/terms/api-services-user-data-policy" className="text-blue-600 hover:text-blue-800 underline">Google API Services User Data Policy</a></li>
                    <li>• Google's Limited Use requirements</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Contact Information</h2>
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6">
                  <h3 className="font-display text-lg font-normal text-slate-800 mb-3">Questions about this Privacy Policy?</h3>
                  <div className="space-y-2 text-slate-600">
                    <p><strong>Email:</strong> privacy@nodayoff.ai</p>
                    <p><strong>Phone:</strong> (310) 861-3287</p>
                    <p><strong>Google API Concerns:</strong> api-compliance@nodayoff.ai</p>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Compliance and Certifications</h2>
                <p className="text-slate-600 mb-4">We maintain compliance with:</p>
                <ul className="space-y-2 text-slate-600">
                  <li>Google API Services User Data Policy and Limited Use requirements</li>
                  <li>OAuth 2.0 security standards</li>
                  <li>California Consumer Privacy Act (CCPA)</li>
                  <li>General Data Protection Regulation (GDPR)</li>
                  <li>SOC 2 Type II security standards</li>
                </ul>
              </section>

              <div className="text-center pt-8 border-t border-slate-200">
                <p className="text-slate-500 text-sm">
                  This Privacy Policy was last updated on January 1, 2025. Previous versions are available upon request.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-12 px-6 bg-white/50">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-center md:items-start gap-3">
            <div className="font-display text-xl text-gradient-purple">
            receptioneer.ai
            </div>
            <p className="text-slate-500 text-center md:text-left text-sm">
              AI receptionists for service businesses
            </p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-6">
              <a href="/" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm">Home</a>
              <a href="/terms" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm">Terms</a>
              <a href="/privacy" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm">Privacy</a>
            </div>
            <div className="text-slate-400 text-xs">
              © 2025 receptioneer.ai powered by nodayoff. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;